/**
 * Portuguese Translations (Português)
 */

import type { Translations } from '../types';

export const pt: Translations = {
  language: 'pt',
  languageName: 'Português',

  common: {
    demo: 'DEMO',
    privacy: 'Privacidade',
    terms: 'Termos',
  },

  privacy: {
    title: 'Política de Privacidade',
    whatWeStore: 'O que armazenamos',
    whatWeStoreDesc:
      'Processamos temporariamente a geometria facial para estimativa de idade. Nenhuma imagem ou modelo biométrico é armazenado após a verificação.',
    whatWeDont: 'O que não armazenamos',
    whatWeDontDesc:
      'Não armazenamos fotos, vídeos ou identificadores biométricos. Seus dados faciais são processados no navegador e descartados imediatamente.',
    whyWeUseIt: 'Por que usamos',
    whyWeUseItDesc:
      'A estimativa de idade ajuda sites a cumprir regulamentações de restrição etária sem exigir documentos pessoais.',
    readFull: 'Leia nossa',
    privacyPolicyLink: 'Política de Privacidade',
  },

  theme: {
    system: 'Sistema',
    light: 'Claro',
    dark: 'Escuro',
  },

  face: {
    noFace: 'Nenhum rosto detectado',
    tooFar: 'Rosto muito longe',
    tooClose: 'Rosto muito perto',
    offCenter: 'Rosto descentralizado',
    poorLighting: 'Iluminação insuficiente',
    good: 'Analisando, aguarde...',
    holdStill: 'Analisando, aguarde...',
    holdStillCountdown: 'Fique parado... {count}',
    processing: 'Processando...',
    looking: 'Nenhum rosto detectado',
    centerFace: 'Rosto descentralizado',
    moveCloser: 'Rosto muito longe',
    moveBack: 'Rosto muito perto',
    keepInPosition: 'Mantenha posição',
    perfect: 'Perfeito',
    centerInFrame: 'Posicione seu rosto no quadro',
    outsideOval: 'Rosto fora do oval',
  },

  liveness: {
    blink: {
      title: 'Pisque os olhos',
      hint: 'Pisque lentamente uma vez',
    },
    smile: {
      title: 'Sorria',
      hint: 'Mostre um sorriso natural',
    },
    lookLeft: {
      title: 'Vire para a esquerda',
      hint: 'Vire a cabeça lentamente para a esquerda',
    },
    lookRight: {
      title: 'Vire para a direita',
      hint: 'Vire a cabeça lentamente para a direita',
    },
    openMouth: {
      title: 'Abra a boca',
      hint: 'Abra bem a boca',
    },
    lookUp: {
      title: 'Olhe para cima',
      hint: 'Incline a cabeça para cima',
    },
    lookDown: {
      title: 'Olhe para baixo',
      hint: 'Incline a cabeça para baixo',
    },
    raiseBrows: {
      title: 'Levante as sobrancelhas',
      hint: 'Levante suas sobrancelhas',
    },
    improveLighting: {
      title: 'Melhore a iluminação',
      hint: 'Vá para um local mais iluminado para continuar',
    },
  },

  document: {
    selectType: 'Selecione o tipo de documento',
    captureFront: 'Capture a frente do documento',
    captureBack: 'Capture o verso do documento',
    processing: 'Processando seu documento...',
    tips: {
      goodLighting: 'Garanta boa iluminação',
      flatSurface: 'Coloque o documento em uma superfície plana',
      allCorners: 'Certifique-se de que todos os cantos estão visíveis',
      noGlare: 'Evite reflexos e sombras',
    },
    types: {
      passport: 'Passaporte',
      idCard: 'Carteira de Identidade',
      driversLicense: 'Carteira de Motorista',
    },
  },

  ui: {
    continue: 'Continuar',
    next: 'Próximo',
    retry: 'Tentar novamente',
    startOver: 'Recomeçar',
    cancel: 'Cancelar',
    success: 'Verificação de Idade Aprovada',
    failure: 'Verificação de Idade Reprovada',
    loading: 'Carregando...',
    error: 'Ocorreu um erro',
    permissionRequired: 'Permissão necessária',
    cameraPermission: 'Acesso à câmera é necessário',
    goBack: 'Voltar',
    switchCamera: 'Trocar câmera',
    completeVerification: 'Concluir',
    continueToId: 'Continuar para Verificação de ID',
  },

  errors: {
    cameraNotFound: 'Nenhuma câmera encontrada neste dispositivo',
    cameraPermissionDenied: 'A permissão da câmera foi negada',
    cameraInUse: 'A câmera está sendo usada por outro aplicativo',
    cameraNotSupported: 'A câmera não suporta as configurações necessárias',
    cameraSecurityError: 'A câmera requer conexão HTTPS',
    networkError: 'Erro de rede. Por favor, verifique sua conexão.',
    timeout: 'Tempo esgotado. Por favor, tente novamente.',
    invalidApiKey: 'Chave de API inválida',
    sessionExpired: 'Sessão expirada. Por favor, recomece.',
    verificationFailed: 'Verificação falhou. Por favor, tente novamente.',
    unknownError: 'Ocorreu um erro inesperado',
    cameraAccessDeniedDetailed: 'Permita o acesso à câmera para continuar.',
    cameraNotFoundDetailed:
      'Nenhuma câmera detectada. Por favor, conecte uma câmera e tente novamente.',
    cameraInUseDetailed:
      'A câmera está sendo usada por outro aplicativo. Por favor, feche-o e tente novamente.',
    cameraOverconstrained:
      'A câmera não suporta a resolução solicitada. Tentando com configurações padrão...',
    networkTimeout:
      'A solicitação demorou muito. Por favor, verifique sua conexão e tente novamente.',
    networkOffline: 'Você parece estar offline. Por favor, verifique sua conexão com a internet.',
    apiGenericError: 'Algo deu errado. Por favor, tente novamente.',
    faceNotDetected: 'Por favor, posicione seu rosto no quadro.',
    livenessFailed: 'Verificação de vivacidade falhou. Por favor, tente novamente.',
    documentCaptureFailed:
      'Não foi possível capturar o documento. Por favor, garanta boa iluminação e tente novamente.',
    initializationFailed: 'Falha ao carregar. Atualize a página e tente novamente.',
    modelLoadFailed:
      'Falha ao carregar os componentes necessários. Por favor, atualize e tente novamente.',
    unexpectedError: 'Ocorreu um erro inesperado. Por favor, tente novamente.',
    configError: 'Erro de configuração. Por favor, contate o suporte.',
  },

  screens: {
    welcome: {
      title: 'Verificação de Idade 18+',
      subtitle: 'Verificação de Idade Anônima',
      startButton: 'Iniciar',
      secureNote: 'Seguro e Criptografado',
      ageTechBy: 'Tecnologia de idade por AppBouncer',
      waitingCamera: 'Aguardando Acesso à Câmera',
      allowCamera: 'Permitir Acesso à Câmera',
      noBiometric: 'Nenhum dado biométrico armazenado',
      loading: 'Carregando...',
      loadingRequirements: 'Carregando requisitos...',
    },
    permissions: {
      title: 'Carregando ...',
      description: 'Por favor, permita o acesso à câmera quando solicitado',
      allowButton: 'Permitir Acesso à Câmera',
      privacyNote: 'Seus dados estão criptografados e seguros',
      goodLighting: 'Boa Iluminação!',
      goodLightingHint: 'Certifique-se de que seu rosto está bem iluminado',
      getReady: 'Prepare-se',
      removeCoverings: 'Remova Coberturas Faciais',
      removeGlasses: 'Remova os Óculos',
    },
    facePosition: {
      title: 'Posicione Seu Rosto',
      hint: 'Centralize o rosto',
      step: 'Passo',
      stepOf: 'Passo {current} de {total}',
      lookingForFace: 'Procurando rosto...',
    },
    livenessCheck: {
      title: 'Verificação de Vivacidade',
      followInstructions: 'Siga as instruções',
      preparing: 'Preparando...',
      complete: 'Vivacidade Verificada!',
      actionAutoDetected: 'A ação será detectada automaticamente',
      failed: 'Verificação de Vivacidade Falhou',
      failedDescription:
        'Não conseguimos verificar sua vivacidade. Por favor, certifique-se de ter boa iluminação, olhe diretamente para a câmera e siga as instruções na tela.',
      retryButton: 'Repetir Verificação de Vivacidade',
      restartButton: 'Recomeçar',
    },
    ageEstimation: {
      title: 'Fique Parado',
      analyzing: 'Analisando...',
      analyzingProgress: 'Analisando rosto {current} de {total}...',
      estimatedAge: 'Idade Estimada',
      holdStill: 'Fique Parado',
      verified: 'Idade Verificada',
      verificationUncertain: 'Idade Incerta',
      estimationComplete: 'Verificação de idade concluída',
      confirmedAge: 'Confirmado {minAge}+ anos',
      couldNotConfirm: 'Não foi possível confirmar idade {minAge}+',
      ageConfirmed: 'Idade Confirmada',
      aboveMinimumAge: 'Você atende ao requisito de idade mínima',
      belowMinimumAge: 'Abaixo da idade mínima',
      documentRequired: 'Documento Necessário',
      uploadDocumentMessage:
        'Por favor, envie um documento de identidade válido para verificar sua idade',
      idHint: 'RG, passaporte ou carteira de motorista',
      confidenceLevels: 'Confiança por Limite de Idade',
      confidence: 'confiança',
      years: 'anos',
      continueToId: 'Continuar para Verificação de ID',
      demoComplete: 'Modo demo completo. Em produção, você seria redirecionado.',
      improveLighting: 'Melhorar Iluminação',
      improveLightingHint: 'Vá para um local mais iluminado para resultados precisos',
    },
    documentSelect: {
      title: 'Selecionar Tipo de Documento',
      description: 'Escolha o tipo de documento de identidade que você vai digitalizar',
      idCard: 'Carteira de Identidade Nacional',
      idCardHint: 'ID emitido pelo governo',
      passport: 'Passaporte',
      passportHint: 'Apenas página da foto',
      driversLicense: 'Carteira de Motorista',
      driversLicenseHint: 'Carteira de motorista válida',
    },
    captureMode: {
      captureFrontSide: 'Capturar Lado Frontal',
      captureBackSide: 'Capturar Lado Traseiro',
      chooseMethod: 'Escolha como capturar seu documento',
      useCamera: 'Usar Câmera',
      useCameraHint: 'Tire uma foto do seu documento',
      uploadFile: 'Enviar Arquivo',
      uploadFileHint: 'Selecionar do seu dispositivo',
    },
    documentCamera: {
      frontSide: 'Lado Frontal',
      backSide: 'Lado Traseiro',
      positionInFrame: 'Posicione o documento no quadro',
      scanFront: 'Digitalizar Frente do Documento',
      scanBack: 'Digitalizar Verso do Documento',
      keepFlat: 'Mantenha o documento plano e bem iluminado',
      capturePhoto: 'Capturar Foto',
    },
    documentPreview: {
      frontPreview: 'Visualização da Frente',
      backPreview: 'Visualização do Verso',
      lookingGood: 'Ficou Ótimo!',
      checkDetails: 'Certifique-se de que todos os detalhes estão claramente visíveis',
      imageQuality: 'Qualidade da imagem',
      qualityGood: 'Boa',
      usePhoto: 'Usar Esta Foto',
      retakePhoto: 'Tirar Outra Foto',
    },
    documentCapture: {
      captureFront: 'Capturar Frente',
      captureBack: 'Capturar Verso',
      alignDocument: 'Alinhe o documento dentro do quadro',
      captureButton: 'Capturar',
      retakeButton: 'Refazer',
      confirmButton: 'Confirmar',
      frontSide: 'Lado Frontal',
      backSide: 'Lado Traseiro',
      positionDocument: 'Posicione seu documento no quadro',
      alignWithFrame: 'Alinhe o documento com o quadro',
      positionFrontId: 'Posicione a frente do ID',
      positionBackId: 'Posicione o verso do ID',
      scanFrontId: 'Digitalizar Frente do ID',
      scanBackId: 'Digitalizar Verso do ID',
      positionWithinFrame: 'Posicione o lado frontal dentro do quadro',
      flipAndScan: 'Vire seu ID e digitalize o verso',
      goodLight: 'Boa iluminação',
      flatSurface: 'Superfície plana',
      frontCaptured: 'Frente capturada',
      captureIdCard: 'Capturar Carteira de Identidade',
      capturePassport: 'Capturar Passaporte',
      captureDriversLicense: 'Capturar Carteira de Motorista',
    },
    processing: {
      title: 'Processando',
      description: 'Por favor aguarde enquanto processamos',
      analyzingDocument: 'Analisando documento...',
      almostDone: 'Quase pronto...',
      faceVerified: 'Rosto verificado',
      livenessConfirmed: 'Vivacidade confirmada',
      processingDocuments: 'Processando documentos...',
    },
    success: {
      title: 'Verificação de Idade Aprovada',
      description: 'Verificação de idade e vivacidade concluída',
      redirecting: 'Redirecionando...',
      summary: 'Resumo',
      faceMatch: 'Correspondência Facial',
      liveness: 'Vivacidade',
      age: 'Idade',
      idDocument: 'Documento de Identidade',
      pass: 'Aprovado',
      fail: 'Reprovado',
      valid: 'Válido',
      notApplicable: 'N/A',
      done: 'Concluído',
      feedbackTitle: 'Avalie sua experiência',
      feedbackPlaceholder: 'Diga-nos como podemos melhorar...',
      skip: 'Pular',
      submit: 'Enviar',
    },
    failure: {
      title: 'Verificação de Idade Reprovada',
      description: 'Não foi possível completar a verificação. Por favor, tente novamente.',
      tryAgainButton: 'Tentar Novamente',
      leaveButton: 'Sair',
      attemptsLeft: '{{count}} tentativa(s) restante(s)',
      noAttemptsLeft: 'Sem mais tentativas disponíveis',
    },
    error: {
      title: 'Configuração Necessária',
      description:
        'Alguns parâmetros obrigatórios estão faltando na URL. Por favor, contate o administrador do site.',
      missingParams: 'Parâmetros Ausentes',
      requiredParams: 'Parâmetros Obrigatórios',
      technicalDetails: 'Detalhes Técnicos',
      urlMustInclude: 'A URL deve incluir estes parâmetros de consulta:',
    },
    underage: {
      title: 'Requisito de Idade Não Atendido',
      description: 'Você deve ter pelo menos {{minAge}} anos para acessar este serviço.',
    },
    consent: {
      title: 'Ajude a Melhorar a Verificação de Idade',
      description:
        'Permita que o AppBouncer colete uma pequena quantidade de dados de uso anônimos (como capturas de feed) para melhorar a verificação de idade e verificações de fallback. Seus dados nunca são vendidos ou fornecidos a terceiros.',
      allowButton: 'Permitir',
    },
    documentConsent: {
      title: 'Documento Necessário',
      description:
        'Para concluir a verificação, precisamos verificar sua idade usando um documento oficial. Você consente em enviar seu documento de identidade?',
      allowButton: 'Continuar com Documento',
      declineButton: 'Recusar',
      privacyNote: 'Seu documento será processado com segurança e excluído após a verificação.',
    },
    selfDeclaration: {
      title: 'Sua Data de Nascimento',
      description: 'Por favor, insira sua data de nascimento para continuar',
      day: 'Dia',
      month: 'Mês',
      year: 'Ano',
      yourAge: 'Sua idade:',
      years: 'anos',
      confirmButton: 'Continuar',
      months: {
        january: 'Janeiro',
        february: 'Fevereiro',
        march: 'Março',
        april: 'Abril',
        may: 'Maio',
        june: 'Junho',
        july: 'Julho',
        august: 'Agosto',
        september: 'Setembro',
        october: 'Outubro',
        november: 'Novembro',
        december: 'Dezembro',
      },
    },
  },

  lighting: {
    warning: 'Problema de Iluminação',
    adjust: 'Ajuste a iluminação',
    lowLight: 'Baixa luminosidade detectada',
    tooBright: 'Muito claro',
    poorContrast: 'Contraste ruim',
    improveLighting: 'Melhore a iluminação',
    moveToLighter: 'Vá para um local mais iluminado',
    videoNotReady: 'Vídeo não está pronto',
  },

  steps: {
    faceDetection: 'Detecção Facial',
    liveness: 'Verificação de Vivacidade',
    age: 'Verificação de Idade',
    documentScan: 'Digitalização de Documento',
    selfDeclaration: 'Autodeclaração',
  },

  status: {
    cameraStarted: 'Câmera iniciada',
    cameraReady: 'Câmera pronta',
    cameraAccessDenied: 'Acesso à câmera negado',
    cameraError: 'Erro de câmera',
    cameraNotAvailable: 'Câmera não disponível',
    noFaceDetected: 'Nenhum rosto detectado',
    livenessVerified: 'Vivacidade verificada',
    livenessCheckFailed: 'Verificação de vivacidade falhou',
    verifyingAge: 'Verificando idade...',
    processingDocument: 'Processando documento...',
    documentProcessed: 'Documento processado com sucesso',
    verificationCancelled: 'Verificação cancelada',
    improveLightingForAge: 'Melhore a iluminação para resultados precisos',
    getReady: 'Prepare-se',
    preparingLiveness: 'Preparando verificação de vivacidade...',
    uploadingSelfie: 'Enviando imagens de selfie...',
    preparingUpload: 'Preparando envio...',
    uploadingFront: 'Enviando documento frontal...',
    uploadingBack: 'Enviando documento traseiro...',
    processingDocuments: 'Processando documentos...',
    documentSubmissionFailed: 'Falha ao enviar documento. Por favor, tente novamente.',
    selectJpegOrPng: 'Por favor, selecione uma imagem JPEG ou PNG',
    fileTooLarge: 'Arquivo muito grande. Máximo de 5MB permitido.',
    errorReadingFile: 'Erro ao ler o arquivo',
    frontDocumentRequired: 'Imagem do documento frontal obrigatória',
    verificationFailedClose: 'Verificação falhou. Você será redirecionado.',
    verificationCompleteClose: 'Verificação concluída! Você será redirecionado.',
    analyzingFace: 'Analisando rosto...',
  },
};
